/**
 * useMediaQuery - Custom hook for responsive design
 *
 * Provides a clean way to detect screen size breakpoints in React components.
 * Works with SSR and handles window resize events efficiently.
 */

import { useState, useEffect, useCallback } from 'react';

// Breakpoint constants matching Tailwind's default breakpoints
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
  '3xl': 1920,
  '4xl': 2560,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Core media query hook
 * @param query - CSS media query string (e.g., '(max-width: 767px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const getMatches = useCallback((q: string): boolean => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(q).matches;
    }
    return false;
  }, []);

  const [matches, setMatches] = useState<boolean>(() => getMatches(query));

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);

    const handleChange = () => {
      setMatches(mediaQueryList.matches);
    };

    // Set initial value
    handleChange();

    // Modern browsers
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
}

/**
 * Check if viewport is mobile (< 640px)
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`);
}

/**
 * Check if viewport is tablet (640px - 1023px)
 */
export function useIsTablet(): boolean {
  return useMediaQuery(
    `(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`
  );
}

/**
 * Check if viewport is desktop (>= 1024px)
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}

/**
 * Check if viewport is small mobile (< 375px)
 * For very small devices like older iPhones
 */
export function useIsSmallMobile(): boolean {
  return useMediaQuery('(max-width: 374px)');
}

/**
 * Check if viewport is at most a certain breakpoint
 */
export function useMaxWidth(breakpoint: BreakpointKey): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS[breakpoint] - 1}px)`);
}

/**
 * Check if viewport is at least a certain breakpoint
 */
export function useMinWidth(breakpoint: BreakpointKey): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[breakpoint]}px)`);
}

/**
 * Get current breakpoint name
 * Useful for conditional rendering based on screen size
 */
export function useBreakpoint(): BreakpointKey | 'xs' {
  const isSm = useMinWidth('sm');
  const isMd = useMinWidth('md');
  const isLg = useMinWidth('lg');
  const isXl = useMinWidth('xl');
  const is2xl = useMinWidth('2xl');
  const is3xl = useMinWidth('3xl');
  const is4xl = useMinWidth('4xl');

  if (is4xl) return '4xl';
  if (is3xl) return '3xl';
  if (is2xl) return '2xl';
  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  return 'xs';
}

/**
 * Check if device prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * Check if device is in portrait orientation
 */
export function useIsPortrait(): boolean {
  return useMediaQuery('(orientation: portrait)');
}

/**
 * Check if device has touch capability
 */
export function useIsTouchDevice(): boolean {
  return useMediaQuery('(hover: none) and (pointer: coarse)');
}

